// #618: Kontaktliste eines Objekts für Dienstleister – nur Name, Lage der Wohnung, Telefon, Mobil, E-Mail.
// Keine Mieten, Bankdaten oder Notizen. Nur wohnende Mieter (aktiv/gekündigt), keine ehemaligen.

import { istWohnend } from '../dashboard/leerstand';
import type { ExportColumn } from '../export/types';
import { mieterName } from './objektDetail';
import type { EinheitDetailRoh, MieterDetailRoh } from './objektDetail';
import type { HausRoh } from './useObjektDetail';

export interface KontaktZeile {
  name: string;
  lage: string;
  telefon: string;
  mobil: string;
  email: string;
}

export const KONTAKT_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'name', label: 'Name', breite: 23 },
  { key: 'lage', label: 'Lage der Wohnung', breite: 19 },
  { key: 'telefon', label: 'Telefon', breite: 16 },
  { key: 'mobil', label: 'Mobil', breite: 16 },
  { key: 'email', label: 'E-Mail', breite: 26 },
];

const t = (s: string | null | undefined) => (s ?? '').trim();
const sortierung = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

function hausText(h: HausRoh | undefined): string {
  if (!h) return '';
  return t(h.bezeichnung) || [t(h.strasse), t(h.hausnummer)].filter(Boolean).join(' ');
}

/** Lage = [Haus,] Wohnung [– Zimmer], Etage. Das Haus nur, wenn das Objekt mehrere Häuser hat. */
function lageText(e: EinheitDetailRoh | undefined, einheiten: Map<string, EinheitDetailRoh>, haeuser: Map<string, HausRoh>, mehrereHaeuser: boolean, freitext: string): string {
  if (!e) return freitext;
  const wohnung = e.uebergeordnete_einheit_id ? einheiten.get(e.uebergeordnete_einheit_id) : undefined;
  const bezeichnung = [t(wohnung?.bezeichnung), t(e.bezeichnung)].filter(Boolean).join(' – ');
  const hausId = e.haus_id ?? wohnung?.haus_id ?? null;
  const haus = mehrereHaeuser && hausId ? hausText(haeuser.get(hausId)) : '';
  const etage = t(e.etage) || t(wohnung?.etage);
  return [haus, bezeichnung, etage && !bezeichnung.toLowerCase().includes(etage.toLowerCase()) ? etage : '']
    .filter(Boolean).join(', ');
}

export function kontaktListe(einheiten: readonly EinheitDetailRoh[], mieter: readonly MieterDetailRoh[], haeuser: readonly HausRoh[]): KontaktZeile[] {
  const einheitJe = new Map(einheiten.map(e => [e.id, e]));
  const hausJe = new Map(haeuser.map(h => [h.id, h]));
  const mehrereHaeuser = haeuser.length > 1;
  return mieter
    .filter(m => istWohnend(m.status))
    .map(m => ({
      name: mieterName(m),
      lage: lageText(m.einheit_id ? einheitJe.get(m.einheit_id) : undefined, einheitJe, hausJe, mehrereHaeuser, t(m.einheit)),
      telefon: t(m.telefon),
      mobil: t(m.mobil),
      email: t(m.email),
    }))
    .sort((a, b) => sortierung.compare(a.lage, b.lage) || sortierung.compare(a.name, b.name));
}
